import { create } from 'zustand';

import type { LayoutCard, RangeValue } from './custom-transfers-slice';
import type { PlayerPosition } from '@api-service/players';

export type PlayerInItem = {
  value: string;
  label: string;
  team: string;
};

/**
 * The filter values the suggestions query actually runs with. Captured when
 * the user applies their filters, so tweaking a filter card never refetches
 * by itself; the drafts only take effect on the next apply.
 */
export type CustomFilterSnapshot = {
  layoutOrder: LayoutCard[];
  noOfTransfers: RangeValue;
  effectiveOwnership: RangeValue;
  playerPrice: RangeValue;
  playerPosition: PlayerPosition | null;
  teamIds: string[];
  playerInItems: PlayerInItem[];
  playerOutIds: string[];
};

type FilterDraftFields = Pick<
  CustomFilterSnapshot,
  | 'layoutOrder'
  | 'noOfTransfers'
  | 'effectiveOwnership'
  | 'playerPrice'
  | 'playerPosition'
  | 'teamIds'
  | 'playerInItems'
  | 'playerOutIds'
>;

/** Shared by the store and the dirty check so field order always matches. */
export const buildFilterSnapshot = (
  state: FilterDraftFields
): CustomFilterSnapshot => ({
  layoutOrder: [...state.layoutOrder],
  noOfTransfers: { ...state.noOfTransfers },
  effectiveOwnership: { ...state.effectiveOwnership },
  playerPrice: { ...state.playerPrice },
  playerPosition: state.playerPosition,
  teamIds: [...state.teamIds],
  playerInItems: [...state.playerInItems],
  playerOutIds: [...state.playerOutIds]
});

type CustomTransfersLocalStore = {
  layoutOrder: LayoutCard[];
  noOfTransfers: RangeValue;
  effectiveOwnership: RangeValue;
  playerPrice: RangeValue;
  playerPosition: PlayerPosition | null;
  teamIds: string[];
  playerInItems: PlayerInItem[];
  playerOutIds: string[];
  disabledPlayerIds: string[];
  pendingExcludePlayerIds: string[];
  pendingPlayerInItems: PlayerInItem[];
  excludePlayerIds: string[];
  activePlayerIdsIn: string;
  showCustomSuggestions: boolean;
  hasPendingRecommendation: boolean;
  appliedFilters: CustomFilterSnapshot | null;
  applyFilters: () => void;
  initialiseFromRecommendedPlayer: (playerId: string) => void;
  clearPendingRecommendation: () => void;
  addLayoutCard: (card: LayoutCard) => void;
  removeLayoutCard: (card: LayoutCard) => void;
  setNoOfTransfers: (min: number, max: number) => void;
  setEffectiveOwnership: (min: number, max: number) => void;
  setPlayerPrice: (min: number, max: number) => void;
  setPlayerPosition: (position: PlayerPosition | null) => void;
  setTeamIds: (ids: string[]) => void;
  setPlayerInItems: (items: PlayerInItem[]) => void;
  setPlayerOutIds: (ids: string[]) => void;
  disablePlayer: (playerId: string) => void;
  restorePlayer: (playerId: string) => void;
  addPendingExcludePlayer: (playerId: string) => void;
  addPendingPlayerIn: (item: PlayerInItem) => void;
  commitRegenerate: () => void;
  dismissSuggestions: () => void;
  resetCustomTransfersLocalStore: () => void;
};

const DEFAULT_NO_OF_TRANSFERS: RangeValue = { min: 1, max: 4 };
const DEFAULT_EFFECTIVE_OWNERSHIP: RangeValue = { min: 0, max: 100 };
const DEFAULT_PLAYER_PRICE: RangeValue = { min: 4, max: 12.5 };

const mergePendingPlayerIns = (
  playerInItems: PlayerInItem[],
  pendingPlayerInItems: PlayerInItem[],
  layoutOrder: LayoutCard[]
): { playerInItems: PlayerInItem[]; layoutOrder: LayoutCard[] } => {
  const existingValues = new Set(playerInItems.map((item) => item.value));
  const additions = pendingPlayerInItems.filter(
    (item) => !existingValues.has(item.value)
  );
  return {
    playerInItems: [...playerInItems, ...additions],
    layoutOrder:
      additions.length > 0 && !layoutOrder.includes('playerIn')
        ? [...layoutOrder, 'playerIn' as const]
        : layoutOrder
  };
};

export const useCustomTransfersStore = create<CustomTransfersLocalStore>(
  (set) => ({
    layoutOrder: [],
    noOfTransfers: DEFAULT_NO_OF_TRANSFERS,
    effectiveOwnership: DEFAULT_EFFECTIVE_OWNERSHIP,
    playerPrice: DEFAULT_PLAYER_PRICE,
    playerPosition: null,
    teamIds: [],
    playerInItems: [],
    playerOutIds: [],
    disabledPlayerIds: [],
    pendingExcludePlayerIds: [],
    pendingPlayerInItems: [],
    excludePlayerIds: [],
    activePlayerIdsIn: '',
    showCustomSuggestions: false,
    hasPendingRecommendation: false,
    appliedFilters: null,
    applyFilters: () =>
      set((state) => ({
        showCustomSuggestions: true,
        appliedFilters: buildFilterSnapshot(state)
      })),
    initialiseFromRecommendedPlayer: (playerId) =>
      set(() => {
        const filters: CustomFilterSnapshot = {
          layoutOrder: ['playerOut'],
          noOfTransfers: DEFAULT_NO_OF_TRANSFERS,
          effectiveOwnership: DEFAULT_EFFECTIVE_OWNERSHIP,
          playerPrice: DEFAULT_PLAYER_PRICE,
          playerPosition: null,
          teamIds: [],
          playerInItems: [],
          playerOutIds: [playerId]
        };
        return {
          ...filters,
          disabledPlayerIds: [],
          pendingExcludePlayerIds: [],
          pendingPlayerInItems: [],
          excludePlayerIds: [],
          activePlayerIdsIn: '',
          showCustomSuggestions: true,
          hasPendingRecommendation: true,
          appliedFilters: buildFilterSnapshot(filters)
        };
      }),
    clearPendingRecommendation: () => set({ hasPendingRecommendation: false }),
    addLayoutCard: (card) =>
      set((state) =>
        state.layoutOrder.includes(card)
          ? state
          : { layoutOrder: [...state.layoutOrder, card] }
      ),
    removeLayoutCard: (card) =>
      set((state) => ({
        layoutOrder: state.layoutOrder.filter((c) => c !== card),
        ...(card === 'noOfTransfers' && {
          noOfTransfers: DEFAULT_NO_OF_TRANSFERS
        }),
        ...(card === 'effectiveOwnership' && {
          effectiveOwnership: DEFAULT_EFFECTIVE_OWNERSHIP
        }),
        ...(card === 'playerPrice' && { playerPrice: DEFAULT_PLAYER_PRICE }),
        ...(card === 'playerPosition' && { playerPosition: null }),
        ...(card === 'team' && { teamIds: [] }),
        ...(card === 'playerIn' && { playerInItems: [] }),
        ...(card === 'playerOut' && { playerOutIds: [] })
      })),
    setNoOfTransfers: (min, max) => set({ noOfTransfers: { min, max } }),
    setEffectiveOwnership: (min, max) =>
      set({ effectiveOwnership: { min, max } }),
    setPlayerPrice: (min, max) => set({ playerPrice: { min, max } }),
    setPlayerPosition: (position) => set({ playerPosition: position }),
    setTeamIds: (ids) => set({ teamIds: ids }),
    setPlayerInItems: (items) => set({ playerInItems: items }),
    setPlayerOutIds: (ids) => set({ playerOutIds: ids }),
    disablePlayer: (playerId) =>
      set((state) => {
        if (state.disabledPlayerIds.includes(playerId)) return state;
        return { disabledPlayerIds: [...state.disabledPlayerIds, playerId] };
      }),
    restorePlayer: (playerId) =>
      set((state) => ({
        disabledPlayerIds: state.disabledPlayerIds.filter(
          (id) => id !== playerId
        ),
        pendingExcludePlayerIds: state.pendingExcludePlayerIds.filter(
          (id) => id !== playerId
        ),
        pendingPlayerInItems: state.pendingPlayerInItems.filter(
          (item) => item.value !== playerId
        )
      })),
    addPendingExcludePlayer: (playerId) =>
      set((state) => {
        if (state.pendingExcludePlayerIds.includes(playerId)) return state;
        return {
          pendingExcludePlayerIds: [...state.pendingExcludePlayerIds, playerId]
        };
      }),
    addPendingPlayerIn: (item) =>
      set((state) => {
        if (
          state.pendingPlayerInItems.some(
            (existing) => existing.value === item.value
          )
        ) {
          return state;
        }
        return {
          pendingPlayerInItems: [...state.pendingPlayerInItems, item]
        };
      }),
    commitRegenerate: () =>
      set((state) => {
        const pendingIds = state.pendingPlayerInItems.map((item) => item.value);
        const existingIds = state.activePlayerIdsIn
          ? state.activePlayerIdsIn.split(',')
          : [];
        const allPlayerIdsIn = [
          ...new Set([...existingIds, ...pendingIds])
        ].join(',');

        const merged = mergePendingPlayerIns(
          state.playerInItems,
          state.pendingPlayerInItems,
          state.layoutOrder
        );

        // Mirror the merge into the applied snapshot: the regenerate already
        // takes effect via activePlayerIdsIn, so the drafts must not read as
        // unapplied changes afterwards.
        const appliedMerged = state.appliedFilters
          ? mergePendingPlayerIns(
              state.appliedFilters.playerInItems,
              state.pendingPlayerInItems,
              state.appliedFilters.layoutOrder
            )
          : null;

        return {
          excludePlayerIds: [
            ...new Set([
              ...state.excludePlayerIds,
              ...state.pendingExcludePlayerIds
            ])
          ],
          activePlayerIdsIn: allPlayerIdsIn,
          playerInItems: merged.playerInItems,
          layoutOrder: merged.layoutOrder,
          disabledPlayerIds: [],
          pendingExcludePlayerIds: [],
          pendingPlayerInItems: [],
          appliedFilters:
            state.appliedFilters && appliedMerged
              ? {
                  ...state.appliedFilters,
                  playerInItems: appliedMerged.playerInItems,
                  layoutOrder: appliedMerged.layoutOrder
                }
              : state.appliedFilters
        };
      }),
    dismissSuggestions: () =>
      set((state) => {
        const merged = mergePendingPlayerIns(
          state.playerInItems,
          state.pendingPlayerInItems,
          state.layoutOrder
        );

        return {
          showCustomSuggestions: false,
          playerInItems: merged.playerInItems,
          layoutOrder: merged.layoutOrder,
          disabledPlayerIds: [],
          pendingExcludePlayerIds: [],
          pendingPlayerInItems: [],
          excludePlayerIds: [],
          activePlayerIdsIn: '',
          appliedFilters: null
        };
      }),
    resetCustomTransfersLocalStore: () =>
      set({
        layoutOrder: [],
        noOfTransfers: DEFAULT_NO_OF_TRANSFERS,
        effectiveOwnership: DEFAULT_EFFECTIVE_OWNERSHIP,
        playerPrice: DEFAULT_PLAYER_PRICE,
        playerPosition: null,
        teamIds: [],
        playerInItems: [],
        playerOutIds: [],
        disabledPlayerIds: [],
        pendingExcludePlayerIds: [],
        pendingPlayerInItems: [],
        excludePlayerIds: [],
        activePlayerIdsIn: '',
        showCustomSuggestions: false,
        hasPendingRecommendation: false,
        appliedFilters: null
      })
  })
);
